
import { defineComponent } from 'vue'
import { mapState, mapGetters, useStore } from 'vuex'
import { useDisk, mergeDiskAlias, getPercentAndBgColor } from '@remote/store/hezi-file/disk'
import { useHeziFileStore } from '@remote/store/hezi-file'
import { formatSize } from '@remote/utils'
import { isLargeViewPort } from '@remote/utils/base'
import { getHeziSpace } from '@remote/api/files'
import { ejectDisk, mountDisk } from '@remote/api/adapter/disk-mqtt'
import { setDiskName } from '@remote/api/adapter/gx-mqtt'
import { getBackerStatus, deleteBackups } from '@remote/api/adapter/shell-mqtt'
import { getHeziAccountInfo } from '@remote/api/adapter/api-http'
import { formatDisk, openAddNetDriveForm, openBackupRestoreDialog, openBackupRestoreResultDialog } from './helper'
import { openActionsSheet } from './operation-dialogs'
import Fail from '@remote/components/Fail.vue'
import modal from '@remote/components/modal'
import Page from '@remote/components/layout/SubPage.vue'
import { checkState } from '@remote/store/utils'
import { toast } from '@remote/utils/dialog'
import Button from '@remote/components/Button.vue'
import Card from './components/Card.vue'
import CardItem from './components/CardItem.vue'
import WarnAffix from './components/WarnAffix.vue'

import { IDiskPartition, IDiskPartitionItem } from '@remote/type/hezi'

// const BACKUP_MIN_UPLOADABLE_SIZE = 1024
// const BACKUP_MIN_DOWNLOADABLE_SIZE = 1024 * 1024 * 100

export default defineComponent({
  components: {
    Fail,
    Page,
    Button,
    Card,
    CardItem,
    WarnAffix,
  },
  setup: () => {
    const store = useStore()
    const diskStore = useDisk()
    const heziFileStore = useHeziFileStore()
    return { store, diskStore, formatSize, heziFileStore }
  },
  data () {
    return {
      cloudSpaceInfo: { limit: 1099511627776, usage: 0, percent: 0, bgColor: '' },
      actionMap: {},
      backupInfo: {
        uploadStatus: true,
        downloadStatus: false,
        uploadable: true,
        downloadable: true,
      },
      svipInfo: {
        isvip: true,
        expire_time: Date.now(),
      },
      svipPayUrl: '',
      isLargeViewPort: isLargeViewPort(),
    }
  },
  computed: {
    ...mapState(['user']),
    ...mapGetters(['isFullScreen']),
    isDeviceOffline () {
      return !this.store.state.status && this.store.state.loading === 0
    },
    isDiskException () {
      return this.diskStore.data.diskException && !this.netDrives?.length
    },
    lastVipDay () {
      const expireTime = new Date(this.svipInfo?.expire_time).getTime()
      const diff = (expireTime - Date.now())
      return Math.floor(diff / (24 * 60 * 60 * 1000))
    },
    backupUploadable () {
      return this.svipInfo.isvip && this.backupInfo.uploadStatus
    },
    backupDownloadable () {
      return (this.svipInfo.isvip || this.lastVipDay >= -30) && this.backupInfo.downloadable
    },
    lowCloudSpace () {
      return this.svipInfo.isvip && !this.backupInfo.uploadable
    },
    isCanRestored () {
      return this.cloudSpaceInfo.usage > 0 && this.backupInfo.downloadStatus && this.lastVipDay >= -30 && !this.isBuildDiskException
    },
    isCanFormatCloudBackup () {
      return this.lastVipDay >= -30
    },
    builtInDisk () {
      return this.diskStore.data.builtInDisk
    },
    isBuildDiskException (): boolean {
      return !!(this.actionMap[this.builtInDisk?.uuid] || this.builtInDisk?.hanging || this.builtInDisk?.formatting || this.builtInDisk?.mounting)
    },
    builtInDiskSize () {
      return formatSize(this.diskStore.data.builtInDisk?.size)
    },
    builtInDiskUsage () {
      return formatSize(this.diskStore.data.builtInDisk?.size - this.diskStore.data.builtInDisk?.free)
    },
    cloudSpaceInfoSize () {
      return formatSize(this.cloudSpaceInfo.limit)
    },
    cloudSpaceInfoUsage () {
      return formatSize(this.cloudSpaceInfo.usage)
    },
    externalDisks () {
      console.log('externalPartitions:', this.diskStore.data.externalPartitions)
      return this.diskStore.data.externalPartitions
    },
    netDrives () {
      return this.diskStore.data.netDrives
    },
    backupErrorMessage () {
      let title = '已开启云备份空间功能'
      let desc = '内置磁盘文件安全保护中'
      let isException = false
      console.log('backupErrorMessage:', this.lastVipDay, this.svipInfo.isvip, this.backupInfo?.uploadable)
      if (this.lastVipDay < 0 && this.lastVipDay > - 30) {
        title = '您的盒子会员已过期，无法继续备份'
        desc = '请及时续费'
        isException = true
      } else if (!this.svipInfo.isvip) {
        title = '您还不是盒子会员，无法使用云备份空间'
        desc = '请开通或激活盒子会员后再使用此功能'
        isException = true
      } else if (!this.backupUploadable) {
        title = '未开启云备份空间'
        desc = '请开启云备份空间功能后使用'
        isException = true
      } else if (this.lowCloudSpace) {
        title = '云备份空间不足'
        desc = '内置磁盘新增内容将不再做备份。您可通过清理内置磁盘已备份的内容，来释放云备份空间。'
        isException = true
      } else if (this.isBuildDiskException || this.builtInDisk?.readonly && this.builtInDisk?.mount) {
        title = '内置磁盘无响应'
        desc = '磁盘无响应，请重启设备'
        isException = true
      }
      console.log('isException', isException)
      return { title, desc, isException }
    },
  },
  mounted () {
    console.log('user', JSON.stringify(this.user))
    this.diskStore.setLoopInterval(6 * 1000, true)
    getHeziSpace()
      .then(res => {
        if (res?.quota) {
          const { limit, usage } = res?.quota || { limit: 0, usage: 0 }
          const size = +limit
          const used = +usage
          const { percent, color } = getPercentAndBgColor((size - used), size)
          this.cloudSpaceInfo = { limit: size, usage: used, percent, bgColor: color }
          this.backupInfo.uploadable = (size - used) / size > 0.05
        }
      })
    getBackerStatus().then(status => {
      if (Array.isArray(status)) {
        const [uploadStatus, downloadStatus] = status
        console.log('getBackerStatus:', status)
        this.backupInfo.uploadStatus = uploadStatus
        this.backupInfo.downloadStatus = downloadStatus
      }
    })
    getHeziAccountInfo().then(res => {
      console.log('getHeziAccountInfo:', res)
      if (res.ret === 0 && res?.data?.vip) {
        this.svipInfo = res.data.vip
        this.svipPayUrl = res.data?.vip?.svip_pay_url
      }
    })
    if (typeof window.bodymovin !== 'undefined') {
      if (!this.deviceToCloudAnimation) this.deviceToCloudAnimation = window.bodymovin.loadAnimation({
        container: document.getElementById('device-to-cloud'),
        path: 'https://oss.shumeipan.cn/hezi/images/static/web/device_to_cloud.json',
        loop: true,
        autoplay: true,
        name: 'device_to_cloud',
      })

      if (!this.cloudToDeviceAnimation) this.cloudToDeviceAnimation = window.bodymovin.loadAnimation({
        container: document.getElementById('cloud-to-device'),
        path: 'https://oss.shumeipan.cn/hezi/images/static/web/cloud_to_device.json',
        loop: true,
        autoplay: true,
        name: 'cloud_to_device',
      })
    }
    window.addEventListener('resize', this.handleOnResize)
  },
  unmounted () {
    this.diskStore.setLoopInterval(60 * 1000)
    this.deviceToCloudAnimation && this.deviceToCloudAnimation?.destroy()
    this.cloudToDeviceAnimation && this.cloudToDeviceAnimation?.destroy()
    window.removeEventListener('resize', this.handleOnResize)
  },
  methods: {
    openAddNetDriveForm (type) {
      openAddNetDriveForm({type})
    },
    renderBuildInDiskState (): string {
      if (!this.builtInDisk) return ''
      const preAction = this.actionMap[this.builtInDisk.uuid]
      if (preAction) {
        return preAction.text
      } else if ((!this.builtInDisk.hanging) && this.builtInDisk.mount) {
        return `已用${formatSize(this.builtInDisk.size - this.builtInDisk.free)}/${formatSize(this.builtInDisk.size)}`
      } else {
        return this.builtInDisk.statusText
      }
    },
    renderPartitionState (disk: IDiskPartition, partition: IDiskPartitionItem): string {
      const preAction = this.actionMap[partition.uuid]
      if (preAction) {
        return preAction.text
      } else if ((!disk.hanging && !partition.hanging) && partition.mount) {
        return `已用${formatSize(partition.size - partition.free)}/${formatSize(partition.size)}`
      } else {
        return partition.statusText
      }
    },
    handleGotoAddExternal () {
      this.$router.push('/disks/add/netdrives')
    },
    handleCloudRestore (action?: 'format' | 'restore') {
      if (action === 'restore') return openBackupRestoreDialog({
        capacity: this.builtInDisk.free > this.cloudSpaceInfo.usage ? 'y' : 'n',
        gotoResult: () => openBackupRestoreResultDialog({}),
      })
      const formatCloudCallback = () => {
        toast({type: 'info', message: '云备份空间正在格式化中'})
        deleteBackups()
          .then(res => {
            if (res.ret === 0) {
              toast({type: 'success', message: '云备份空间格式化成功'})
            } else {
              toast({type: 'error', message: '云备份空间格式化失败'})
            }
          }).catch(() => {
            toast({type: 'error', message: '云备份空间格式化失败'})
          })
      }

      const formatCloudPrams = {
        title: '格式化云备份空间',
        paragraphs: [
          '1. 立即格式化操作，将会清空当前云备份空间全部内容。',
          '2. 若备份功能为开启状态，内置磁盘文件将再次备份到云备份空间。',
        ],
      }

      const restoreAction = {
        id: 'restore',
        text: '恢复数据',
        callback: () => {
          this.$router.push({
            path: '/disks/restore',
            query: {
              capacity: this.builtInDisk.free > this.cloudSpaceInfo.usage ? 'y' : 'n',
            },
          })
        },
      }
      // 可以恢复就可以格式化
      const actions: any = []
      if (!action) {
        actions.push({ id: 'format', text: '格式化', callback: formatCloudCallback })
        if (this.isCanRestored && !this.builtInDisk?.readonly) {
          actions.push(restoreAction)
        }
        openActionsSheet({
          ...formatCloudPrams,
          actions,
        })
      } else if (action === 'format') {
        openActionsSheet({
          ...formatCloudPrams,
          process: {
            action,
            callback: formatCloudCallback,
          },
          actions,
        })
      } else {
        openActionsSheet({
          title: '云盘备份恢复',
          actions: [restoreAction],
        })
      }
    },
    async handleDiskAction (disk: IDiskPartitionItem, action: 'format' | 'mount' | 'eject') {
      let errMsg = ''
      const checkStateRet = checkState()
      if (checkStateRet) return Promise.resolve(checkStateRet)

      const handleReject = (err) => {
        console.log('handleReject', err)
        delete this.actionMap[disk.uuid]
        delete this.actionMap[disk.pname]
        toast({type: 'error', message: err || errMsg})
        errMsg = err || errMsg
      }

      const handleResolve = async (res) => {
        console.log('handleResolve', res)
        if (res?.ret && res.ret !== 0 && !res.msg.includes('timed out')) {
          handleReject(res.msg)
        } else {
          if (action === 'format') {
            const { disks } = await this.diskStore.checkDeviceDisk()
            const nextDisk = disks?.find(item => item.uuid === disk.uuid)
            // 格式化后uuid会变化
            if (!disk?.mount && nextDisk?.mount) {
              delete this.actionMap[disk.uuid]
              delete this.actionMap[disk.pname]
              toast({type: 'success', message: '磁盘格式化成功'})
            } else {
              setTimeout(async () => {
                const { disks } = await this.diskStore.checkDeviceDisk()
                const nextDisk = disks?.find(item => item.uuid === disk.uuid)
                delete this.actionMap[disk.uuid]
                delete this.actionMap[disk.pname]
                const isSuccess = !nextDisk || nextDisk.mount
                toast({type: isSuccess ? 'success' : 'error', message: isSuccess ? '磁盘格式化成功' : '磁盘格式化失败'})
              }, 6000)
            }
          } else if (action === 'mount') {
            const { disks } = await this.diskStore.checkDeviceDisk()
            const nextDisk = disks?.find(item => item.uuid === disk.uuid)
            // 激活后会生成mount挂载点
            if (nextDisk.mount) {
              delete this.actionMap[disk.uuid]
              delete this.actionMap[disk.pname]
              toast({type: 'success', message: '磁盘激活成功'})
            } else {
              setTimeout(async () => {
                const { disks } = await this.diskStore.checkDeviceDisk()
                const nextDisk = disks?.find(item => item.uuid === disk.uuid)
                delete this.actionMap[disk.uuid]
                delete this.actionMap[disk.pname]
                toast({type: nextDisk.mount ? 'success' : 'error', message: nextDisk.mount ? '磁盘激活成功' : '磁盘激活失败'})
              }, 3000)
            }
          } else {
            const { externalPartitions: disks } = await this.diskStore.checkDeviceDisk()
            const nextDisk = disks?.find(item => item.name === disk.name)
            // 磁盘弹出后会该磁盘会不存在
            if (!nextDisk) {
              delete this.actionMap[disk.name]
              errMsg = ''
              toast({type: 'success', message: '磁盘弹出成功'})
            }
          }
        }
      }

      switch (action) {
        case 'format':
          errMsg = '磁盘格式化失败'
          this.actionMap[disk.uuid] = {
            text: '磁盘格式化中...',
            validatorKey: 'type',
          }
          this.actionMap[disk.pname] = {
            text: '磁盘格式化中，请耐心等待',
          }
          toast({type: 'info', message: '磁盘正在格式化中，请不要关闭设备电源'})
          formatDisk(disk)
            .then(handleResolve, handleReject)
          break
        case 'mount':
          errMsg = '磁盘激活失败'
          this.actionMap[disk.uuid] = {
            text: '磁盘激活中...',
            validatorKey: 'mount',
          }
          this.actionMap[disk.pname] = {
            text: '磁盘激活中，请耐心等待',
          }
          toast({type: 'info', message: '磁盘正在激活中，请不要关闭设备电源'})
          mountDisk(disk.uuid)
            .then(handleResolve, handleReject)

          break
        case 'eject':
          errMsg = '磁盘弹出失败'
          this.actionMap[disk.name] = {
            text: '磁盘弹出中...',
          }
          await ejectDisk(disk.name)
            .then(handleResolve, handleReject)
          break
      }

      return Promise.resolve(errMsg)
    },
    handleOnFormatBuiltInDisk (skip?: boolean) {
      const action = !this.diskStore.data.builtInDisk?.mount ? 'mount' : 'format'
      const callback = () => {
        this.handleDiskAction(this.diskStore.data.builtInDisk, action)
      }
      if (skip && action === 'mount') return callback()

      openActionsSheet({
        process: skip ? {
          action: action,
          callback: callback,
        } : null,
        title: '是否格式化内置磁盘？',
        paragraphs: ['立即格式化操作，将会清空内置磁盘全部内容。云备份空间数据不受影响。'],
        disk: this.diskStore.data.builtInDisk,
        isBuiltIn: true,
        actions: [
          { 
            id: action, text: action === 'format' ? '格式化' : '激活', 
            callback,
          },
        ],
      })
    },
    handleOnEjectExternalDisk (disk: IDiskPartition) {
      const callback = () => {
        return this.handleDiskAction(disk, 'eject')
      }
      if (this.isLargeViewPort) {
        modal.Delete({
          title: `确认要弹出 [${disk.mode || disk.name}] 磁盘吗？`,
          cancelText: '取消',
          okText: '立即弹出',
          loadingText: '弹出中...',
          onOk: callback,
        })
      } else {
        openActionsSheet({
          title: '是否弹出该磁盘？',
          actions: [
            { id: 'eject', text: '弹出磁盘', callback },
          ],
        })
      }
    },
    handleRename (disk: IDiskPartitionItem, newName: string) {
      const checkStateRet = checkState()
      if (checkStateRet) return Promise.resolve(false)

      return setDiskName([{
        ori_name: disk.name,
        disk_name: newName,
        path: disk.mount,
      }]).then(res => {
        if (res.ret === 0) {
          toast({type: 'success', message: '磁盘重命名成功'}) 
          mergeDiskAlias({
            [disk.mount]: newName,
          })
          this.diskStore.checkDeviceDisk()
          return true
        } else {
          toast({type: 'error', message: res.msg || '磁盘重命名失败'})
          return false
        }
      })
    },
    handleOnPartitionAction (disk: IDiskPartitionItem, action?: 'format' | 'rename') {
      console.log('handleOnPartitionAction', disk)
      const renameCallback = (newName: string) => {
        this.handleRename(disk, newName)
      }

      const formatDiskCallback = () => {
        return this.handleDiskAction(disk, 'format')
      }

      if (action) {
        openActionsSheet({
          process: {
            action,
            callback: action === 'format' ? formatDiskCallback : renameCallback,
          },
          disk,
          actions: [
            { id: 'format', text: '格式化', callback: formatDiskCallback },
          ],
        })
      } else {
        openActionsSheet({
          disk,
          actions: [
            { id: 'format', text: '格式化', callback: formatDiskCallback },
            { id: 'rename', text: '重命名', callback: renameCallback },
          ],
        })
      }
    },
    handleMountDisk (disk: IDiskPartitionItem) {
      if (disk.type && disk.type !== 'unknow') {
        console.log('进行激活挂载')
        this.handleDiskAction(disk, 'mount')
      } else {
        const formatDiskCallback = () => {
          return this.handleDiskAction(disk, 'format')
        }
        openActionsSheet({
          disk,
          process: {
            action: 'format',
            callback: formatDiskCallback,
          },
          actions: [
            { id: 'format', text: '格式化', callback: formatDiskCallback },
          ],
        })
      }
    },
    handleToPaySVip () {
      this.svipPayUrl && window.open(this.svipPayUrl)
    },
    handleOnClickReadonlyPartionToast (readonly: boolean) {
      if (readonly) toast({type: 'warn', message: '当前磁盘仅支持读取，不支持写入'})
    },
    handleOnRemoveNetDrive (id: string, noConfirm?: boolean, name  = '') {
      const callback = () => {
        const checkStateRet = checkState()
        if (checkStateRet) return Promise.resolve(checkStateRet)
        return this.diskStore.removeNetDrives(id)
      }
      if (noConfirm) {
        modal.Delete({
          title: name ? `确认要移除 [${name}] 的外部设备吗？` : '确认要移除该外部设备吗？',
          cancelText: '取消',
          okText: '立即移除',
          loadingText: '移除中...',
          onOk: callback,
        })
      } else {
        openActionsSheet({
          title: '是否移除该外部存储目录？',
          actions: [
            { id: 'remove', text: '移除', callback },
          ],
        })
      }
    },
    handleOnCloseCallback () {
      this.heziFileStore.refreshFiles()
    },
    handleOnResize () {
      this.isLargeViewPort = isLargeViewPort()
    },
  },
})
